import axios from 'axios';

import { env } from 'config';
import { RequisitesStatus, UserRole } from 'enums';
import {
  PaginatedData,
  QueryParams,
  Requisites,
  RequisitesCustomerAssignment,
} from 'types';
import { apiUtils } from 'utils';

const url = `${env.apiUrl}/requisites`;

const getAllForTraderAsRole =
  (role: UserRole) =>
  async (traderId: string): Promise<Requisites[]> =>
    (await axios.get(`${url}/${role}/trader/${traderId}`)).data;

const getAllPaginated = async (
  params: QueryParams,
): Promise<PaginatedData<Requisites>> =>
  (await apiUtils.getWithQueryParams(url, params)).data;

const getForGroupAsRole =
  (role: UserRole) =>
  async (groupId: string): Promise<Requisites[]> =>
    (await axios.get(`${url}/${role}/group/${groupId}`)).data;

const getAllPaginatedAsOperator = async (
  params: QueryParams,
): Promise<PaginatedData<Requisites>> =>
  (await apiUtils.getWithQueryParams(`${url}/operator`, params)).data;

const getAllPaginatedAsOperatorArchived = async (
  params: QueryParams,
): Promise<PaginatedData<Requisites>> =>
  (await apiUtils.getWithQueryParams(`${url}/operator/archived`, params)).data;

const getAllMyPaginated = async (
  params: QueryParams,
): Promise<PaginatedData<Requisites>> =>
  (await apiUtils.getWithQueryParams(`${url}/my/paginated`, params)).data;

const getAllMyWhiteTriangle = async (): Promise<Requisites[]> =>
  (await axios.get(`${url}/my/white-triangle`)).data;

const getAllMyPaginatedArchived = async (
  params: QueryParams,
): Promise<PaginatedData<Requisites>> =>
  (await apiUtils.getWithQueryParams(`${url}/my/paginated/archived`, params))
    .data;

const getAllMy = async (): Promise<Requisites[]> =>
  (await axios.get(`${url}/my`)).data;

const getAllForCounterparties = async (
  shopId: string,
  counterpartyId?: string,
): Promise<Requisites[]> =>
  (
    await axios.get(`${url}/shop-counterparties/${shopId}`, {
      params: { counterpartyId },
    })
  ).data;

const create = async (requisites: Partial<Requisites>): Promise<Requisites> =>
  (await axios.post(url, requisites)).data;

const update = async ({
  id,
  requisites,
}: {
  id: string;
  requisites: Partial<Requisites>;
}): Promise<Requisites> => (await axios.patch(`${url}/${id}`, requisites)).data;

const updateOrderAutomation = async ({
  id,
  requisites,
}: {
  id: string;
  requisites: Partial<Requisites>;
}): Promise<Requisites> =>
  (await axios.patch(`${url}/${id}/order-automation`, requisites)).data;

const updateStatus = async ({
  id,
  requisites,
}: {
  id: string;
  requisites: Partial<Requisites>;
}): Promise<Requisites> =>
  (await axios.patch(`${url}/${id}/status`, requisites)).data;

const updateGroupStatus = async ({
  id,
  status,
}: {
  id: string;
  status: RequisitesStatus;
}): Promise<Requisites[]> =>
  (await axios.patch(`${url}/group/${id}/status`, { status })).data;

const unblock = async (id: string): Promise<Requisites> =>
  (await axios.patch(`${url}/${id}/unblock`)).data;

const batchUnblock = async (
  params?: Partial<QueryParams>,
): Promise<Requisites> =>
  (await axios.post(`${url}/batch-unblock`, null, { params })).data;

const block = async (params: {
  id: string;
  blockedMessage: string;
}): Promise<Requisites> =>
  (
    await axios.patch(`${url}/${params.id}/block`, {
      blockedMessage: params.blockedMessage,
    })
  ).data;

const batchBlock = async ({
  blockedMessage,
  params,
}: {
  blockedMessage: string;
  params?: Partial<QueryParams>;
}): Promise<Requisites> =>
  (await axios.post(`${url}/batch-block`, { blockedMessage }, { params })).data;

const findOne = async (id: string): Promise<Requisites> =>
  (await axios.get(`${url}/${id}`)).data;

const remove = async (id: string): Promise<Requisites> =>
  (await axios.delete(`${url}/${id}`)).data;

const archive = async (id: string): Promise<Requisites> =>
  (await axios.post(`${url}/${id}/archive`)).data;

const verify = async (data: {
  id: string;
  disableFutureVerification: boolean;
}): Promise<Requisites> =>
  (
    await axios.post(`${url}/${data.id}/verify`, {
      disableFutureVerification: data.disableFutureVerification,
    })
  ).data;

const batchVerify = async ({
  disableFutureVerification,
  params,
}: {
  disableFutureVerification: boolean;
  params?: Partial<QueryParams>;
}): Promise<Requisites> =>
  (
    await axios.post(
      `${url}/batch-verify`,
      { disableFutureVerification },
      { params },
    )
  ).data;

const restore = async (id: string): Promise<Requisites> =>
  (await axios.post(`${url}/${id}/restore`)).data;

const getAllPaginatedArchived = async (
  params: QueryParams,
): Promise<PaginatedData<Requisites>> =>
  (await apiUtils.getWithQueryParams(`${url}/archived`, params)).data;

const updateCustomerLimits = async ({
  id,
  customerLimits,
}: {
  id: string;
  customerLimits: number[];
}): Promise<Requisites> =>
  (await axios.patch(`${url}/${id}/customer-limits`, { customerLimits })).data;

const getCustomerAssignments = async (
  id: string,
): Promise<RequisitesCustomerAssignment[]> =>
  (await axios.get(`${url}/${id}/customer-assignments`)).data;

export const requisitesApi = {
  getAllForTraderAsRole,
  getAllPaginatedArchived,
  getAllPaginated,
  getForGroupAsRole,
  getAllPaginatedAsOperator,
  getAllPaginatedAsOperatorArchived,
  getAllMyPaginated,
  getAllMyWhiteTriangle,
  getAllMyPaginatedArchived,
  getAllMy,
  create,
  update,
  updateOrderAutomation,
  findOne,
  remove,
  updateStatus,
  updateGroupStatus,
  verify,
  batchVerify,
  block,
  batchBlock,
  unblock,
  batchUnblock,
  archive,
  restore,
  updateCustomerLimits,
  getAllForCounterparties,
  getCustomerAssignments,
};
